import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Buildings,
  Plus,
  PencilSimple,
  Key,
  ArrowClockwise,
  Power,
  MagnifyingGlass,
  CalendarBlank,
  Motorcycle,
  X,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    cnpj: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    license_days: 30,
    password: "",
  });
  const [renewDays, setRenewDays] = useState(30);
  const [newPassword, setNewPassword] = useState("");
  const [editData, setEditData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/companies`, {
        withCredentials: true,
      });
      setCompanies(data);
    } catch (err) {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Preencha nome, email e senha");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/companies`, formData, {
        withCredentials: true,
      });
      toast.success("Empresa cadastrada com sucesso!");
      setShowForm(false);
      setFormData({
        name: "",
        cnpj: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        license_days: 30,
        password: "",
      });
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar empresa");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenew = async () => {
    if (!showRenewModal) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/companies/${showRenewModal}/renew`,
        { days: renewDays },
        { withCredentials: true },
      );
      toast.success(data.message);
      setShowRenewModal(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao renovar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!showPasswordModal || !newPassword) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/companies/${showPasswordModal}/reset-password`,
        { password: newPassword },
        { withCredentials: true },
      );
      toast.success("Senha alterada com sucesso!");
      setShowPasswordModal(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (company) => {
    try {
      await axios.put(
        `${API}/admin/companies/${company.id}`,
        { active: !company.active },
        { withCredentials: true },
      );
      toast.success(company.active ? "Empresa desativada" : "Empresa ativada");
      fetchCompanies();
    } catch (err) {
      toast.error("Erro ao atualizar status");
    }
  };

  const handleEdit = async () => {
    if (!showEditModal) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/companies/${showEditModal}`, editData, {
        withCredentials: true,
      });
      toast.success("Empresa atualizada!");
      setShowEditModal(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired = (expiry) => {
    if (!expiry) return false;
    return new Date(expiry) < new Date();
  };

  const getDaysRemaining = (expiry) => {
    if (!expiry) return 0;
    const diff = new Date(expiry) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const filtered = companies.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.cnpj && c.cnpj.includes(searchTerm)),
  );

  return (
    <Layout title="Administração" showBack backTo="/dashboard">
      <div className="space-y-4 pb-4">
        {/* Search + Add */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative w-full sm:flex-1">
            <MagnifyingGlass
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"
            />
            <input
              type="text"
              placeholder="Buscar empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field !pl-10 w-full"
              data-testid="admin-search-input"
            />
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary w-full sm:w-auto flex-none flex items-center justify-center gap-2 px-6 whitespace-nowrap"
            data-testid="admin-add-company-btn"
          >
            <Plus size={18} weight="bold" />
            <span>Nova Empresa</span>
          </button>
        </div>

        {/* Create Form */}
        {showForm && (
          <form
            onSubmit={handleCreate}
            className="card p-4 sm:p-6 space-y-4"
            data-testid="admin-create-form"
          >
            <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
              <Buildings size={20} className="text-[#00E676]" />
              Nova Empresa
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <input
                className="input-field w-full"
                placeholder="Nome da Empresa *"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                data-testid="company-name-input"
              />
              <input
                className="input-field w-full"
                placeholder="CNPJ"
                value={formData.cnpj}
                onChange={(e) =>
                  setFormData({ ...formData, cnpj: e.target.value })
                }
                data-testid="company-cnpj-input"
              />
              <input
                className="input-field w-full"
                type="email"
                placeholder="Email de Acesso *"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                data-testid="company-email-input"
              />
              <input
                className="input-field w-full"
                placeholder="Telefone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                data-testid="company-phone-input"
              />
              <input
                className="input-field w-full"
                placeholder="Endereço"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
              <input
                className="input-field w-full"
                placeholder="Cidade"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
              />
              <input
                className="input-field w-full"
                placeholder="Estado"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
              />
              <input
                className="input-field w-full"
                type="number"
                placeholder="Dias de Licença"
                value={formData.license_days}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    license_days: parseInt(e.target.value) || 30,
                  })
                }
                data-testid="company-license-days-input"
              />
              <input
                className="input-field w-full sm:col-span-2"
                type="password"
                placeholder="Senha de Acesso *"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                data-testid="company-password-input"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary w-full sm:w-auto px-6"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
                data-testid="company-submit-btn"
              >
                {submitting ? "Cadastrando..." : "Cadastrar Empresa"}
              </button>
            </div>
          </form>
        )}

        {/* Companies List */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card p-8 text-center">
            <Buildings size={48} className="text-[#A1A1AA] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">Nenhuma empresa cadastrada</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((company) => {
              const expired = isExpired(company.license_expiry);
              const daysLeft = getDaysRemaining(company.license_expiry);

              return (
                <div
                  key={company.id}
                  className="card p-4 sm:p-5"
                  data-testid={`company-card-${company.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                    <div className="overflow-hidden">
                      <h3 className="font-bold text-lg truncate">
                        {company.name}
                      </h3>
                      <p className="text-sm text-[#A1A1AA] break-all">
                        {company.email}
                      </p>
                      {company.cnpj && (
                        <p className="text-sm text-[#A1A1AA] mt-0.5">
                          CNPJ: {company.cnpj}
                        </p>
                      )}
                    </div>
                    <div className="self-start">
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-medium ${
                          !company.active
                            ? "bg-red-500/10 text-red-500"
                            : expired
                              ? "bg-orange-500/10 text-orange-500"
                              : "bg-[#00E676]/10 text-[#00E676]"
                        }`}
                      >
                        {!company.active
                          ? "Desativada"
                          : expired
                            ? "Expirada"
                            : "Ativa"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA] mb-5">
                    <span className="flex items-center gap-1.5">
                      <CalendarBlank size={16} />
                      {expired ? "Expirada" : `${daysLeft} dias restantes`}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Motorcycle size={16} />
                      {company.moto_count || 0} motos
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                    <button
                      onClick={() => {
                        setShowEditModal(company.id);
                        setEditData({
                          name: company.name,
                          cnpj: company.cnpj || "",
                          phone: company.phone || "",
                          address: company.address || "",
                          city: company.city || "",
                          state: company.state || "",
                        });
                      }}
                      className="text-xs sm:text-sm px-3 py-2 sm:py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 flex items-center justify-center sm:justify-start gap-1.5 hover:bg-[#222] transition-colors"
                      data-testid={`edit-company-${company.id}`}
                    >
                      <PencilSimple size={16} /> Editar
                    </button>
                    <button
                      onClick={() => {
                        setShowRenewModal(company.id);
                        setRenewDays(30);
                      }}
                      className="text-xs sm:text-sm px-3 py-2 sm:py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center justify-center sm:justify-start gap-1.5 hover:bg-blue-500/20 transition-colors"
                      data-testid={`renew-company-${company.id}`}
                    >
                      <ArrowClockwise size={16} /> Renovar
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordModal(company.id);
                        setNewPassword("");
                      }}
                      className="text-xs sm:text-sm px-3 py-2 sm:py-1.5 rounded-lg bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 flex items-center justify-center sm:justify-start gap-1.5 hover:bg-yellow-500/20 transition-colors"
                      data-testid={`reset-pw-company-${company.id}`}
                    >
                      <Key size={16} /> Senha
                    </button>
                    <button
                      onClick={() => handleToggleActive(company)}
                      className={`text-xs sm:text-sm px-3 py-2 sm:py-1.5 rounded-lg flex items-center justify-center sm:justify-start gap-1.5 transition-colors ${
                        company.active
                          ? "bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                          : "bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20 hover:bg-[#00E676]/20"
                      }`}
                      data-testid={`toggle-company-${company.id}`}
                    >
                      <Power size={16} />{" "}
                      {company.active ? "Desativar" : "Ativar"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Renew Modal */}
        {showRenewModal && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setShowRenewModal(null)}
          >
            <div
              className="card p-5 sm:p-6 w-full max-w-sm space-y-5"
              onClick={(e) => e.stopPropagation()}
              data-testid="renew-modal"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg">Renovar Licença</h3>
                <button
                  onClick={() => setShowRenewModal(null)}
                  className="p-1 hover:bg-white/10 rounded-md transition-colors"
                >
                  <X size={20} className="text-[#A1A1AA]" />
                </button>
              </div>
              <input
                type="number"
                className="input-field w-full"
                placeholder="Dias"
                value={renewDays}
                onChange={(e) => setRenewDays(parseInt(e.target.value) || 30)}
                data-testid="renew-days-input"
              />
              <div className="flex flex-wrap gap-2 justify-center">
                {[30, 90, 180, 365].map((d) => (
                  <button
                    key={d}
                    onClick={() => setRenewDays(d)}
                    className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${renewDays === d ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10" : "border-white/10 text-[#A1A1AA] hover:border-white/30"}`}
                  >
                    {d} dias
                  </button>
                ))}
              </div>
              <button
                onClick={handleRenew}
                disabled={submitting}
                className="btn-primary w-full py-3"
                data-testid="renew-confirm-btn"
              >
                {submitting ? "Renovando..." : `Renovar por ${renewDays} dias`}
              </button>
            </div>
          </div>
        )}

        {/* Password Modal */}
        {showPasswordModal && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setShowPasswordModal(null)}
          >
            <div
              className="card p-5 sm:p-6 w-full max-w-sm space-y-5"
              onClick={(e) => e.stopPropagation()}
              data-testid="password-modal"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg">Alterar Senha</h3>
                <button
                  onClick={() => setShowPasswordModal(null)}
                  className="p-1 hover:bg-white/10 rounded-md transition-colors"
                >
                  <X size={20} className="text-[#A1A1AA]" />
                </button>
              </div>
              <input
                type="password"
                className="input-field w-full"
                placeholder="Nova senha (min. 6 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
              <button
                onClick={handleResetPassword}
                disabled={submitting || newPassword.length < 6}
                className="btn-primary w-full py-3"
                data-testid="password-confirm-btn"
              >
                {submitting ? "Alterando..." : "Confirmar Nova Senha"}
              </button>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowEditModal(null)}
          >
            <div
              className="card p-5 sm:p-6 w-full max-w-md space-y-4 my-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="edit-modal"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg">Editar Empresa</h3>
                <button
                  onClick={() => setShowEditModal(null)}
                  className="p-1 hover:bg-white/10 rounded-md transition-colors"
                >
                  <X size={20} className="text-[#A1A1AA]" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs text-[#A1A1AA] ml-1">Nome</label>
                  <input
                    className="input-field w-full"
                    placeholder="Nome"
                    value={editData.name || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#A1A1AA] ml-1">CNPJ</label>
                  <input
                    className="input-field w-full"
                    placeholder="CNPJ"
                    value={editData.cnpj || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, cnpj: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#A1A1AA] ml-1">
                    Telefone
                  </label>
                  <input
                    className="input-field w-full"
                    placeholder="Telefone"
                    value={editData.phone || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, phone: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs text-[#A1A1AA] ml-1">
                    Endereço
                  </label>
                  <input
                    className="input-field w-full"
                    placeholder="Endereço"
                    value={editData.address || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, address: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#A1A1AA] ml-1">Cidade</label>
                  <input
                    className="input-field w-full"
                    placeholder="Cidade"
                    value={editData.city || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, city: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#A1A1AA] ml-1">Estado</label>
                  <input
                    className="input-field w-full"
                    placeholder="Estado"
                    value={editData.state || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, state: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={handleEdit}
                  disabled={submitting}
                  className="btn-primary w-full py-3"
                  data-testid="edit-confirm-btn"
                >
                  {submitting ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
